import { css, Theme } from '@emotion/react';
import { XCircle } from '@phosphor-icons/react';
import { EventType } from 'common/EventConstants';
import { useApi } from 'client/lib/trpc';
import { useUser } from 'client/lib/User';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { Button } from 'client/components/Button';
import { Hero, HeroProps } from 'client/components/Hero/Hero';
import { CloseAnimation } from 'client/components/Other/CloseAnimation';
import { Post } from 'client/components/Post';
import { Typography } from 'client/components/Typography';

export type TrialOnboardingProps = Partial<HeroProps>;

export const TrialOnboarding = (props: TrialOnboardingProps) => {
  const user = useUser();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const projectName = useDeploymentClientState((state) => state.project?.name || '');
  const isTrialOnboardingOpen = useDeploymentClientState((state) => state.flags.isTrialOnboardingOpen || false);
  const api = useApi();

  const handleClose = () => {
    if (!deploymentID) return;
    api.status.eventCreate.mutate({
      eventTypeID: EventType.TRIAL_TUTORIAL_DISMISS,
      deploymentID,
    });
    useDeploymentClientState.setState((state) => ({ ...state, flags: { ...state.flags, isTrialOnboardingOpen: false } }));
  };

  if (!user.anonymousTrial) return null;

  return (
    <CloseAnimation open={isTrialOnboardingOpen} css={animationStyle} onClose={handleClose}>
      {({ ref, onAnimateClose }) => (
        <Post outline ref={ref} css={postStyle}>
          <Button icon={<XCircle size={24} weight="fill" />} onClick={onAnimateClose} css={closeStyle} />

          <Hero title="Congrats!" css={heroStyle} {...props}>
            <Typography>
              🎉 You have just launched your first {projectName} project with Diploi! Here's what you should try out next:
            </Typography>

            <ol css={stepperStyle}>
              <li>
                <Typography>Open Your Live Site</Typography>
                <Typography variant="body2">Open your site live in your browser by clicking the "App" URL.</Typography>
              </li>
              <li>
                <Typography>Edit the Code</Typography>
                <Typography variant="body2">Open a code editor by clicking the "Open in Browser IDE" -button.</Typography>
              </li>
            </ol>
          </Hero>
        </Post>
      )}
    </CloseAnimation>
  );
};

const circleCheckIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Cpath d='M128 24a104 104 0 1 0 104 104A104.2 ` +
  `104.2 0 0 0 128 24Zm49.5 85.8-58.6 56a8.1 8.1 0 0 1-5.6 2.2 7.7 7.7 0 0 1-5.5-2.2l-29.3-28a8 8 0 1 1 11-11.6l23.8 ` +
  `22.7 53.2-50.7a8 8 0 0 1 11 11.6Z' fill='${theme.color.primary[100].replace('#', '%23')}' /%3E%3C/svg%3E");`;

const animationStyle = css`
  --margin: 32px;
`;

const postStyle = (theme: Theme) => css`
  --padding: 44px;
  margin-bottom: 32px;
  border-color: ${theme.color.primary[100]};
  background-color: ${theme.color.background};
`;

const closeStyle = (theme: Theme) => css`
  position: absolute;
  top: -24px;
  right: -24px;
  background: transparent;
  opacity: 0.6;
  z-index: 1;

  svg {
    color: ${theme.color.white};
  }

  &:hover {
    background: transparent;
    box-shadow: none;
    opacity: 1;
  }
`;

const heroStyle = (theme: Theme) => css`
  margin-top: 0;
  padding: 0;
  border-color: ${theme.color.primary[100]};

  > aside {
    display: none;
  }

  > div {
    padding: 0;
  }

  > div > header {
    margin-bottom: 8px;
  }

  > div > .typography.body1 {
    color: ${theme.color.grey[100]};
  }

  ::after {
    content: none;
  }
`;

const stepperStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: 36px;
  margin: 0;
  margin-top: 32px;
  padding: 0;
  list-style: none;
  counter-reset: stepper;

  ${theme.mediaQuery.large(css`
    flex-direction: row;
  `)}

  > li {
    position: relative;
    counter-increment: stepper;

    &::before {
      content: counter(stepper);
      position: absolute;
      top: -2px;
      left: 0;
      display: flex;
      justify-content: center;
      line-height: 26px;
      width: 27px;
      height: 27px;
      font-family: ${theme.font.header};
      font-weight: bold;
      border-radius: 50%;
      font-size: 1rem;
      border: 2px solid ${theme.color.primary[100]};
      color: ${theme.color.primary[100]};
    }

    &[data-done='true'] {
      &::before {
        content: '';
        background-size: calc(100% + 8px);
        background-position: -4px -4px;
        background-image: ${circleCheckIconURI(theme)};
        color: ${theme.color.white};
      }
    }

    > .body1 {
      margin-bottom: 4px;
      margin-left: calc(29px + 8px);
      font-weight: bold;
    }

    > p {
      max-width: 48ch;
    }
  }
`;
